/* =========================================================================
   The recipe store — in-memory list, persistence, migration, import/export.

   The rest of the app talks to this module and never touches IndexedDB
   directly. `recipes` stays a plain array so the ported view code reads the
   same way it did in iteration 1.
   ========================================================================= */

import {
  allRecipes, putRecipe, putManyRecipes, deleteRecipe, clearRecipes,
  getMeta, setMeta,
} from './db.js'
import { dataUrlToBlob, blobToDataUrl, releaseBlobUrl } from './images.js'
import { normaliseRecipe, tidyRecipeText, uid, CAT_EMOJI } from './model.js'
import { SEED_RECIPES } from './seed.js'

/** Bump when the house-style rules change, to re-tidy stored recipes once. */
const FORMAT_VERSION = 2

export let recipes = []

/** Last thing that went wrong while saving, surfaced by the UI banner. */
export let lastSaveError = null

const LEGACY_KEY = 'familyRecipes.v1'

/* ---------------- load & migrate ---------------- */

export async function loadStore() {
  const existing = await allRecipes()
  if (existing.length) {
    recipes = existing
    const tidied = await applyHouseStyleOnce()
    return { source: 'indexeddb', count: recipes.length, tidied }
  }

  // Nothing in IndexedDB. Has this browser got an iteration-1 collection?
  const migrated = await migrateFromLocalStorage()
  if (migrated) {
    recipes = await allRecipes()
    return { source: 'migrated', count: recipes.length }
  }

  // Has the user deliberately emptied the collection? Don't re-seed if so.
  if (await getMeta('seeded')) {
    recipes = []
    return { source: 'empty', count: 0 }
  }

  const seeded = SEED_RECIPES.map((r, i) => normaliseRecipe(r, i))
  await putManyRecipes(seeded)
  await setMeta('seeded', true)
  recipes = seeded
  return { source: 'seeded', count: recipes.length }
}

/**
 * One-off pass applying the current house style to recipes that were saved
 * before those rules existed. Runs once per FORMAT_VERSION bump, then never
 * again — so a recipe you deliberately reworded afterwards stays as you left it.
 *
 * @returns {Promise<number>} how many recipes were actually changed
 */
async function applyHouseStyleOnce() {
  if (await getMeta('formatVersion') === FORMAT_VERSION) return 0

  let changed = 0
  for (const r of recipes) {
    const before = JSON.stringify([r.ingredients, r.method])
    tidyRecipeText(r)
    if (JSON.stringify([r.ingredients, r.method]) !== before) changed++
  }

  if (changed) await putManyRecipes(recipes)
  await setMeta('formatVersion', FORMAT_VERSION)
  return changed
}

/**
 * One-time migration of an iteration-1 localStorage collection.
 * The original data is deliberately LEFT IN PLACE as a safety net —
 * we never destroy the only copy of someone's recipes.
 */
async function migrateFromLocalStorage() {
  let raw = null
  try { raw = localStorage.getItem(LEGACY_KEY) } catch { return false }
  if (!raw) return false

  let parsed
  try { parsed = JSON.parse(raw) } catch { return false }
  if (!Array.isArray(parsed) || !parsed.length) return false

  const converted = parsed.map((r, i) => {
    const n = normaliseRecipe(r, i)
    n.heroImage = typeof r.heroImage === 'string' ? dataUrlToBlob(r.heroImage) : (r.heroImage ?? null)
    n.images = (r.images || []).map(im => (typeof im === 'string' ? dataUrlToBlob(im) : im)).filter(Boolean)
    return n
  })

  await putManyRecipes(converted)
  await setMeta('seeded', true)
  await setMeta('migratedFromLocalStorage', new Date().toISOString())
  return true
}

/* ---------------- mutations ---------------- */

function reindex() {
  recipes.forEach((r, i) => { r.pos = i })
}

export function byId(id) { return recipes.find(r => r.id === id) }

/** Persist a single recipe. Returns true on success. */
export async function saveRecipe(r) {
  try {
    await putRecipe(r)
    lastSaveError = null
    return true
  } catch (e) {
    lastSaveError = e
    return false
  }
}

/** Persist the whole collection (used after reorder / import / delete). */
export async function saveAll() {
  try {
    reindex()
    await putManyRecipes(recipes)
    lastSaveError = null
    return true
  } catch (e) {
    lastSaveError = e
    return false
  }
}

export async function addRecipe(data) {
  const r = normaliseRecipe({
    ...data,
    id: uid(),
    emoji: CAT_EMOJI[data.category] || '🍽️',
    createdAt: new Date().toISOString(),
  }, 0)
  recipes.unshift(r)
  reindex()
  await saveAll()
  return r
}

export async function removeRecipe(id) {
  const r = byId(id)
  if (r) {
    releaseBlobUrl(r.heroImage)
    ;(r.images || []).forEach(releaseBlobUrl)
  }
  recipes = recipes.filter(x => x.id !== id)
  reindex()
  try { await deleteRecipe(id) } catch (e) { lastSaveError = e }
  await saveAll()
}

/* ---------------- export ---------------- */

/**
 * Build the exportable array. Photos become data URLs so the file is a single
 * self-contained .json — readable by this app, by iteration 1, and by anyone
 * you send it to.
 */
export async function buildExport() {
  const out = []
  for (const r of recipes) {
    out.push({
      ...r,
      heroImage: await blobToDataUrl(r.heroImage),
      images: await Promise.all((r.images || []).map(blobToDataUrl)),
    })
  }
  return out
}

/* ---------------- import ---------------- */

/**
 * Merge an exported collection in.
 * Same semantics as iteration 1: incoming wins on a matching id, new ids are
 * appended. Data-URL photos are converted to Blobs on the way in.
 */
export async function importCollection(data) {
  if (!Array.isArray(data)) throw new Error('bad-format')

  const map = new Map(recipes.map(r => [r.id, r]))
  let added = 0, updated = 0

  data.forEach((raw, i) => {
    const n = normaliseRecipe(raw, i)
    n.heroImage = typeof raw.heroImage === 'string' ? dataUrlToBlob(raw.heroImage) : (raw.heroImage ?? null)
    n.images = (raw.images || [])
      .map(im => (typeof im === 'string' ? dataUrlToBlob(im) : im))
      .filter(Boolean)

    if (map.has(n.id)) {
      // keep the existing position so the list doesn't reshuffle on re-import
      n.pos = map.get(n.id).pos
      updated++
    } else {
      added++
    }
    map.set(n.id, n)
  })

  recipes = [...map.values()].sort((a, b) => (a.pos ?? 0) - (b.pos ?? 0))
  reindex()
  await setMeta('seeded', true)
  const ok = await saveAll()
  if (!ok) throw lastSaveError || new Error('save-failed')
  return { added, updated, total: data.length }
}

/** Replace everything with the 4 original recipes. */
export async function resetToSeed() {
  await clearRecipes()
  recipes = SEED_RECIPES.map((r, i) => normaliseRecipe(r, i))
  await putManyRecipes(recipes)
  await setMeta('seeded', true)
}
